import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Dialog, DialogContent, Button, IconButton, TextField, MenuItem,
  CircularProgress, Tooltip
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DataObjectIcon from '@mui/icons-material/DataObject'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SendIcon from '@mui/icons-material/Send'
import hipersendService from '../../../services/hipersend'
import Text from '../../Text'
import { TemplatePreviewBubble } from '../../TemplatePreviewBubble'
import {
  buildCustomTemplateDraft, extractBodyVariables, describeApiError,
  buildSendSnippet, TEMPLATE_CATEGORIES, STATUS_COLORS,
  IDIOMA_PADRAO, IDIOMAS_SUPORTADOS, nomeDoIdioma
} from '../../../libs/metaTemplates'

const EMPTY_FORM = {
  name: '',
  language: IDIOMA_PADRAO,
  category: 'UTILITY',
  header: '',
  body: '',
  examples: [],
  footer: '',
  buttons: [],
}

/**
 * Dialog "Criar template": formulário guiado (nome snake_case, categoria com
 * descrição, cabeçalho fixo opcional, corpo com variáveis {{n}} + exemplos,
 * footer e botões opcionais) com preview ao vivo em bolha de WhatsApp. No
 * submit, valida no backend (preview) e instala (install) — a criação real na
 * Graph é feita pelo master.
 *
 * `initialForm` (opcional) semeia o formulário — é o que permite "Personalizar
 * modelo": um recomendado já resolvido chega convertido em form e o cliente
 * ajusta antes de instalar. Sem ele, abre em branco (comportamento padrão).
 * `title` (opcional) troca o título do cabeçalho.
 *
 * `editTarget` (opcional) põe o dialog em MODO EDIÇÃO (corrigir e reenviar):
 * em vez de instalar, o submit chama o /edit do master no registro informado
 * ({ _id, metaReviewStatus }). Nome e idioma ficam travados (são a identidade
 * do template na WABA) e a categoria trava quando o modelo está APROVADO —
 * regras da própria Meta, espelhadas do backend.
 */
function CreateTemplateDialog({ open, onClose, api, secret, onInstalled, initialForm = null, title = 'Criar template', editTarget = null }) {
  const [form, setForm] = useState(EMPTY_FORM)
  const [formErrors, setFormErrors] = useState([])
  const [submitError, setSubmitError] = useState(null)
  const [warnings, setWarnings] = useState([])
  const [submitting, setSubmitting] = useState(false)
  // O preview retornou avisos e estamos aguardando o usuário confirmar o envio
  // ("Enviar mesmo assim") — garante que os warnings do contrato sejam vistos.
  const [pendingConfirm, setPendingConfirm] = useState(false)

  // Ao abrir, semeia o formulário: com initialForm (personalização) ou em branco.
  // Semear no open — e não só no mount — garante que reabrir com outro modelo
  // recarregue os dados certos, sem arrastar edições da sessão anterior.
  useEffect(() => {
    if (!open) return
    // Criando, o idioma é o do modelo de partida quando ele está no catálogo
    // (personalizar um recomendado em espanhol cria em espanhol); fora da lista,
    // cai no padrão. Editando, o idioma é a identidade do modelo na Meta e fica
    // como está.
    const semente = initialForm || EMPTY_FORM
    const idiomaInicial = IDIOMAS_SUPORTADOS.includes(semente.language) ? semente.language : IDIOMA_PADRAO
    setForm(editTarget ? semente : { ...semente, language: idiomaInicial })
    setFormErrors([])
    setSubmitError(null)
    setWarnings([])
    setPendingConfirm(false)
    // initialForm é lido no momento do open (o pai o define antes de abrir);
    // não entra nas deps pra não re-semear e apagar o que o cliente já digitou.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Qualquer mudança no formulário invalida a confirmação pendente: os avisos
  // podem não valer mais pro novo conteúdo, então o preview roda de novo.
  useEffect(() => {
    setPendingConfirm(false)
  }, [form])

  const setField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }, [])

  const isEdit = !!editTarget
  // A Meta não muda a categoria de um template APROVADO — o campo trava.
  const categoryLocked = isEdit &&
    String(editTarget?.metaReviewStatus || '').toUpperCase() === 'APPROVED'

  // Oferece os idiomas do catálogo (ver IDIOMAS_SUPORTADOS). Na edição de um
  // modelo antigo em outro idioma ele entra na lista só pra aparecer — o campo
  // fica travado (nome e idioma são a identidade do modelo na Meta). O envio
  // sai no idioma em que o modelo está instalado, então não há aviso de idioma.
  const idiomas = isEdit && !IDIOMAS_SUPORTADOS.includes(form.language)
    ? [form.language]
    : IDIOMAS_SUPORTADOS

  const variables = useMemo(() => extractBodyVariables(form.body), [form.body])

  // Insere a próxima variável útil no fim do corpo: o menor número que falta
  // na sequência (corpo com {{1}} e {{3}} ganha {{2}}, não outro {{3}}).
  const handleInsertVariable = useCallback(() => {
    setForm((prev) => {
      const current = extractBodyVariables(prev.body)
      let next = 1
      while (current.includes(next)) next += 1
      const separator = prev.body && !prev.body.endsWith(' ') && !prev.body.endsWith('\n') ? ' ' : ''
      return { ...prev, body: `${prev.body}${separator}{{${next}}}` }
    })
  }, [])

  const setExample = useCallback((index, value) => {
    setForm((prev) => {
      const examples = [...prev.examples]
      examples[index] = value
      return { ...prev, examples }
    })
  }, [])

  const addButton = useCallback(() => {
    setForm((prev) => {
      if (prev.buttons.length >= 3) return prev
      return { ...prev, buttons: [...prev.buttons, { type: 'QUICK_REPLY', text: '', url: '', urlExample: '' }] }
    })
  }, [])

  const updateButton = useCallback((index, field, value) => {
    setForm((prev) => {
      const buttons = prev.buttons.map((b, i) => (i === index ? { ...b, [field]: value } : b))
      return { ...prev, buttons }
    })
  }, [])

  const removeButton = useCallback((index) => {
    setForm((prev) => ({ ...prev, buttons: prev.buttons.filter((_, i) => i !== index) }))
  }, [])

  // Preview ao vivo: monta um template "melhor esforço" mesmo com o formulário
  // incompleto — validação de verdade só acontece no submit.
  const previewTemplate = useMemo(() => {
    const components = []
    const header = String(form.header || '').trim()
    if (header) components.push({ type: 'HEADER', format: 'TEXT', text: header })
    components.push(
      { type: 'BODY', text: form.body || 'Digite o corpo da mensagem para ver o preview...' },
    )
    const footer = form.footer.trim()
    if (footer) components.push({ type: 'FOOTER', text: footer })
    const buttons = form.buttons
      .filter((b) => String(b.text || '').trim())
      .map((b) => (
        String(b.type).toUpperCase() === 'URL'
          ? { type: 'URL', text: b.text.trim(), url: String(b.url || '').trim() }
          : { type: 'QUICK_REPLY', text: b.text.trim() }
      ))
    if (buttons.length > 0) components.push({ type: 'BUTTONS', buttons })
    return { name: form.name || 'novo_modelo', language: form.language, category: form.category, components }
  }, [form])

  // Os exemplos são guardados na ordem da lista de variáveis, mas o preview
  // preenche por posição (values[n-1] vira o {{n}}). Remapeia pelo NÚMERO da
  // variável pra que, mesmo com numeração fora de sequência (ex.: {{2}} e
  // {{3}}), cada exemplo apareça na variável certa.
  const previewValues = useMemo(() => {
    const values = []
    variables.forEach((v, i) => {
      values[v - 1] = String(form.examples[i] || '').trim()
    })
    return values
  }, [variables, form.examples])

  // Snippet de envio montado do MESMO objeto que alimenta o preview: o cliente
  // sai daqui com o modelo submetido e com o texto que o ERP dele precisa
  // mandar, em vez de descobrir isso só depois da aprovação.
  const sendSnippet = useMemo(() => {
    const comExemplos = {
      ...previewTemplate,
      components: previewTemplate.components.map((c) => (
        c.type === 'BODY' && previewValues.length
          ? { ...c, example: { body_text: [previewValues.map((v) => v || '')] } }
          : c
      ))
    }
    return buildSendSnippet(comExemplos)
  }, [previewTemplate, previewValues])

  const [snippetCopiado, setSnippetCopiado] = useState(false)

  const copiarSnippet = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(sendSnippet)
      setSnippetCopiado(true)
      setTimeout(() => setSnippetCopiado(false), 2000)
    } catch (e) {
      setSnippetCopiado(false)
    }
  }, [sendSnippet])

  const resetAndClose = useCallback(() => {
    setForm(EMPTY_FORM)
    setFormErrors([])
    setSubmitError(null)
    setWarnings([])
    setPendingConfirm(false)
    onClose()
  }, [onClose])

  const handleSubmit = useCallback(async () => {
    setSubmitError(null)

    // 1) validação local (rápida, sem rede)
    const { template, errors } = buildCustomTemplateDraft(form)
    setFormErrors(errors)
    if (errors.length > 0 || !template) return

    setSubmitting(true)
    let installAttempted = false
    try {
      // 2) validação/normalização no backend, sem efeito colateral
      const previewResult = await hipersendService.metaTemplates.preview(api, secret, { template })
      const previewWarnings = Array.isArray(previewResult?.warnings) ? previewResult.warnings : []
      setWarnings(previewWarnings)

      // Preview trouxe avisos que o usuário ainda não viu: para aqui pro
      // usuário ler — o próximo clique ("Enviar mesmo assim") conclui o envio.
      if (previewWarnings.length > 0 && !pendingConfirm) {
        setPendingConfirm(true)
        return
      }

      // 3) edição: corrige o template que JÁ existe na Meta (volta pra análise);
      //    criação: registra local e submete o create na Graph.
      installAttempted = true
      if (isEdit) {
        await hipersendService.metaTemplates.edit(api, secret, {
          templateId: editTarget._id,
          template,
        })
      } else {
        await hipersendService.metaTemplates.install(api, secret, { template })
      }

      if (onInstalled) {
        onInstalled(
          isEdit
            ? `Modelo "${template.name}" corrigido e reenviado para análise da Meta.`
            : `Modelo "${template.name}" enviado para análise da Meta.`
        )
      }
      setForm(EMPTY_FORM)
      setFormErrors([])
      setWarnings([])
      setPendingConfirm(false)
      onClose()
    } catch (err) {
      setSubmitError(describeApiError(err))
      // Se o install/edit chegou a rodar, o registro pode ter mudado no master
      // (ex.: install "failed" com botão de reenviar, edit com lastError novo)
      // — recarrega a lista pra tela refletir mesmo com o dialog aberto. Sem
      // mensagem de sucesso na snackbar, só o refresh.
      if (installAttempted && onInstalled) onInstalled(null)
    } finally {
      setSubmitting(false)
    }
  }, [form, pendingConfirm, api, secret, onInstalled, onClose, isEdit, editTarget])

  return (
    <Dialog open={open} onClose={submitting ? undefined : resetAndClose} maxWidth="md" fullWidth>
      <div className="flex items-center justify-between px-5 py-4">
        <div>
          <Text className="text-lg font-black">{title}</Text>
          <Text className="text-xs font-light opacity-70">
            {isEdit
              ? 'Corrija o conteúdo e reenvie para a análise da Meta — sem excluir o modelo'
              : 'Novo modelo de mensagem oficial para esta conexão'}
          </Text>
        </div>
        <IconButton size="small" onClick={resetAndClose} disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <DialogContent dividers>
        {/* Aviso fixo: todo modelo passa por análise da Meta */}
        <div
          className="flex items-start gap-2 p-3 rounded-md mb-4"
          style={{ backgroundColor: `${STATUS_COLORS.blue}14`, borderLeft: `3px solid ${STATUS_COLORS.blue}` }}
        >
          <InfoOutlinedIcon style={{ fontSize: 18, color: STATUS_COLORS.blue, marginTop: 2 }} />
          <Text className="text-xs font-light leading-relaxed">
            Todo modelo criado passa por <b>análise da Meta</b> (normalmente de 1 a 24 horas) antes
            de poder ser usado nos envios. Você acompanha o status na lista de modelos desta tela.
          </Text>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Coluna do formulário */}
          <div className="space-y-4">
            <TextField
              label="Nome do modelo"
              size="small"
              fullWidth
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="aviso_de_vencimento"
              disabled={isEdit}
              helperText={
                isEdit
                  ? 'O nome é a identidade do modelo na Meta — não muda na edição'
                  : 'Em snake_case: só letras minúsculas, números e _ (ex.: aviso_de_vencimento)'
              }
            />

            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Idioma"
                size="small"
                select
                fullWidth
                value={form.language}
                onChange={(e) => setField('language', e.target.value)}
                disabled={isEdit}
              >
                {idiomas.map((codigo) => (
                  <MenuItem key={codigo} value={codigo}>{nomeDoIdioma(codigo)}</MenuItem>
                ))}
              </TextField>

              <TextField
                label="Categoria"
                size="small"
                select
                fullWidth
                value={form.category}
                onChange={(e) => setField('category', e.target.value)}
                disabled={categoryLocked}
              >
                {TEMPLATE_CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </TextField>
            </div>

            <Text className="text-xs font-light opacity-70 -mt-2 leading-snug">
              {categoryLocked
                ? 'A Meta não permite mudar a categoria de um modelo aprovado.'
                : TEMPLATE_CATEGORIES.find((c) => c.value === form.category)?.description}
            </Text>

            <TextField
              label="Cabeçalho (opcional)"
              size="small"
              fullWidth
              value={form.header}
              onChange={(e) => setField('header', e.target.value)}
              placeholder="Ultrafibra - Aviso Importante"
              inputProps={{ maxLength: 60 }}
              helperText="Linha em destaque no topo da mensagem, até 60 caracteres (texto fixo, sem variáveis)"
            />

            <div>
              <TextField
                label="Corpo da mensagem"
                size="small"
                fullWidth
                multiline
                minRows={4}
                value={form.body}
                onChange={(e) => setField('body', e.target.value)}
                placeholder={'Olá {{1}}, sua fatura de {{2}} vence em {{3}}.'}
                helperText="Use {{1}}, {{2}}... para os dados que mudam a cada envio"
              />
              <Button
                size="small"
                variant="text"
                startIcon={<DataObjectIcon style={{ fontSize: 15 }} />}
                onClick={handleInsertVariable}
                sx={{ mt: 0.5, fontSize: 12 }}
              >
                Inserir variável
              </Button>
            </div>

            {variables.length > 0 && (
              <div className="space-y-2">
                <Text className="text-xs font-bold uppercase opacity-70">
                  Exemplos das variáveis (obrigatório para a análise da Meta)
                </Text>
                {variables.map((v, i) => (
                  <TextField
                    key={v}
                    label={`Exemplo para {{${v}}}`}
                    size="small"
                    fullWidth
                    value={form.examples[i] || ''}
                    onChange={(e) => setExample(i, e.target.value)}
                    placeholder={v === 1 ? 'Maria' : 'R$ 150,00'}
                  />
                ))}
              </div>
            )}

            <TextField
              label="Rodapé (opcional)"
              size="small"
              fullWidth
              value={form.footer}
              onChange={(e) => setField('footer', e.target.value)}
              inputProps={{ maxLength: 60 }}
              helperText="Linha discreta no fim da mensagem, até 60 caracteres"
            />

            <div>
              <div className="flex items-center justify-between">
                <Text className="text-xs font-bold uppercase opacity-70">Botões (opcional)</Text>
                <Tooltip title={form.buttons.length >= 3 ? 'Máximo de 3 botões' : 'Adicionar botão'} arrow>
                  <span>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<AddIcon style={{ fontSize: 15 }} />}
                      onClick={addButton}
                      disabled={form.buttons.length >= 3}
                      sx={{ fontSize: 12 }}
                    >
                      Adicionar botão
                    </Button>
                  </span>
                </Tooltip>
              </div>
              {form.buttons.map((b, i) => {
                const isUrlButton = String(b.type).toUpperCase() === 'URL'
                // URL com {{n}} é dinâmica: a Meta exige uma URL de exemplo
                // completa (o mesmo example que a validação do master cobra).
                const isDynamicUrl = isUrlButton && /\{\{/.test(String(b.url || ''))
                return (
                  <div key={i} className="mt-2">
                    <div className="flex items-start gap-2">
                      <TextField
                        select
                        size="small"
                        value={b.type}
                        onChange={(e) => updateButton(i, 'type', e.target.value)}
                        sx={{ width: 150, flexShrink: 0 }}
                      >
                        <MenuItem value="QUICK_REPLY">Resposta rápida</MenuItem>
                        <MenuItem value="URL">Abrir link</MenuItem>
                      </TextField>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Texto do botão"
                        value={b.text}
                        onChange={(e) => updateButton(i, 'text', e.target.value)}
                        inputProps={{ maxLength: 25 }}
                      />
                      {isUrlButton && (
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="https://exemplo.com.br"
                          value={b.url}
                          onChange={(e) => updateButton(i, 'url', e.target.value)}
                        />
                      )}
                      <IconButton size="small" onClick={() => removeButton(i)} sx={{ mt: 0.25 }}>
                        <DeleteOutlineIcon style={{ fontSize: 18 }} />
                      </IconButton>
                    </div>
                    {isDynamicUrl && (
                      // Alinhado sob os campos de texto/URL: desconta o seletor
                      // de tipo à esquerda (150 + gap 8) e a lixeira à direita
                      // (IconButton small 28 + gap 8).
                      <div className="mt-1.5" style={{ marginLeft: 158, marginRight: 36 }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="URL de exemplo"
                          placeholder="https://exemplo.com.br/fatura/ABC123"
                          value={b.urlExample || ''}
                          onChange={(e) => updateButton(i, 'urlExample', e.target.value)}
                          helperText={'A URL tem variável {{n}} — a Meta exige uma amostra da URL completa, com a variável já substituída'}
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {formErrors.length > 0 && (
              <div
                className="p-3 rounded-md space-y-1"
                style={{ backgroundColor: `${STATUS_COLORS.red}12`, borderLeft: `3px solid ${STATUS_COLORS.red}` }}
              >
                {formErrors.map((e, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <ErrorOutlineIcon style={{ fontSize: 14, color: STATUS_COLORS.red, marginTop: 2 }} />
                    <Text className="text-xs font-light" style={{ color: STATUS_COLORS.red }}>{e}</Text>
                  </div>
                ))}
              </div>
            )}

            {submitError && (
              <div
                className="p-3 rounded-md flex items-start gap-1.5"
                style={{ backgroundColor: `${STATUS_COLORS.red}12`, borderLeft: `3px solid ${STATUS_COLORS.red}` }}
              >
                <ErrorOutlineIcon style={{ fontSize: 14, color: STATUS_COLORS.red, marginTop: 2 }} />
                <Text className="text-xs font-light" style={{ color: STATUS_COLORS.red }}>{submitError}</Text>
              </div>
            )}

            {warnings.length > 0 && (
              <div
                className="p-3 rounded-md space-y-1"
                style={{ backgroundColor: `${STATUS_COLORS.yellow}14`, borderLeft: `3px solid ${STATUS_COLORS.yellow}` }}
              >
                {pendingConfirm && (
                  <Text className="text-xs font-bold" style={{ color: STATUS_COLORS.yellow }}>
                    O modelo ainda não foi enviado. Revise os avisos abaixo e clique em
                    "Enviar mesmo assim" para concluir.
                  </Text>
                )}
                {warnings.map((w, i) => (
                  <Text key={i} className="text-xs font-light" style={{ color: STATUS_COLORS.yellow }}>{w}</Text>
                ))}
              </div>
            )}
          </div>

          {/* Coluna do preview ao vivo */}
          <div>
            <Text className="text-xs font-bold uppercase opacity-70 mb-2">Preview ao vivo</Text>
            <div
              className="rounded-md px-5 py-6 flex justify-start min-h-[260px]"
              style={{
                backgroundColor: '#0b141a',
                backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
                backgroundSize: '20px 20px',
              }}
            >
              <div className="ml-2 w-full flex justify-start">
                <TemplatePreviewBubble
                  template={previewTemplate}
                  values={previewValues}
                />
              </div>
            </div>
            <Text className="text-[11px] font-light opacity-60 mt-2 leading-snug">
              As variáveis aparecem preenchidas com os exemplos informados. No envio real, seu
              sistema substitui cada {'{{n}}'} pelos dados do cliente.
            </Text>

            {/* Corpo do envio. Fica aqui junto do preview de propósito: o cliente
                termina de desenhar o modelo já sabendo o que o ERP dele precisa
                mandar, em vez de descobrir depois da aprovação. */}
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <Text className="text-xs font-bold uppercase opacity-70">
                  Corpo do envio (metatags)
                </Text>
                <Button size="small" onClick={copiarSnippet}>
                  {snippetCopiado ? 'Copiado!' : 'Copiar'}
                </Button>
              </div>
              <pre
                className="rounded-md p-3 text-[11px] leading-relaxed overflow-x-auto whitespace-pre-wrap break-all"
                style={{ backgroundColor: 'rgba(127,127,127,.12)', margin: 0 }}
              >
                {sendSnippet}
              </pre>
              <Text className="text-[11px] font-light opacity-60 mt-2 leading-snug">
                É este texto que o seu sistema envia em /messages/sendMessage para disparar o
                modelo. Cada {'%VariableNumeric%'} é uma variável do corpo, na ordem — troque os
                exemplos pelos dados reais do cliente. Só funciona depois que a Meta aprovar.
              </Text>
            </div>
          </div>
        </div>
      </DialogContent>

      <div className="flex items-center justify-end gap-2 px-5 py-3">
        <Button size="small" onClick={resetAndClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <SendIcon style={{ fontSize: 15 }} />}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting
            ? 'Enviando...'
            : pendingConfirm
              ? 'Enviar mesmo assim'
              : isEdit ? 'Reenviar para análise' : 'Enviar para análise'}
        </Button>
      </div>
    </Dialog>
  )
}

export default CreateTemplateDialog
